import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  IconButton,
  Divider,
  Checkbox,
  ListItemText,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  formControl: {
    margin: theme.spacing(1, 0),
    minWidth: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  fieldContainer: {
    width: "100%",
  },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
  },
  windowsSection: {
    marginTop: theme.spacing(2),
  },
  windowRow: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
    position: "relative",
  },
  windowRowFields: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  dayChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  removeButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  mediaSection: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  mediaPreview: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1),
    border: "1px solid #ddd",
    borderRadius: 4,
    marginTop: 4,
  },
  mediaThumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 4,
  },
  mediaName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const DAY_OPTIONS = [
  { value: 1, label: "autoReplyRuleModal.form.windows.days.mon" },
  { value: 2, label: "autoReplyRuleModal.form.windows.days.tue" },
  { value: 3, label: "autoReplyRuleModal.form.windows.days.wed" },
  { value: 4, label: "autoReplyRuleModal.form.windows.days.thu" },
  { value: 5, label: "autoReplyRuleModal.form.windows.days.fri" },
  { value: 6, label: "autoReplyRuleModal.form.windows.days.sat" },
  { value: 0, label: "autoReplyRuleModal.form.windows.days.sun" },
];

const emptyWindow = {
  windowType: "weekly",
  daysOfWeek: "",
  specificDate: "",
  startTime: "",
  endTime: "",
  endDayOffset: 0,
};

const AutoReplyRuleSchema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").required("Required"),
  matchType: Yup.string().oneOf(["keywords", "any"]),
  keywords: Yup.string().when("matchType", {
    is: (matchType) => matchType !== "any",
    then: Yup.string().required("Required"),
    otherwise: Yup.string().notRequired(),
  }),
  tagFilter: Yup.string().oneOf(["all", "with_tag", "without_tag"]),
  whatsappId: Yup.number().required("Required"),
  quickAnswerId: Yup.number().required("Required"),
  windows: Yup.array().of(
    Yup.object().shape({
      windowType: Yup.string().oneOf(["weekly", "specific_date"]).required(),
      daysOfWeek: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().notRequired(),
      }),
      specificDate: Yup.string().when("windowType", {
        is: "specific_date",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().notRequired(),
      }),
      startTime: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().nullable(),
      }),
      endTime: Yup.string().when("windowType", {
        is: "weekly",
        then: Yup.string().required("Required"),
        otherwise: Yup.string().nullable(),
      }),
      endDayOffset: Yup.number().min(0).max(6),
    })
  ),
});

const AutoReplyRuleModal = ({ open, onClose, ruleId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const fileInputRef = useRef(null);
  const { whatsApps } = useContext(WhatsAppsContext);
  const [quickAnswers, setQuickAnswers] = useState([]);
  const [allRules, setAllRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);

  const initialState = {
    name: "",
    keywords: "",
    matchType: "keywords",
    tagFilter: "all",
    active: true,
    priority: 0,
    whatsappId: "",
    quickAnswerId: "",
    windows: [],
    mediaPath: "",
    mediaMimetype: "",
    mediaOriginalName: "",
    mediaOrder: "media_first",
    parentRuleId: "",
    fireAfterAssigned: false,
    userScope: "all",
    userIds: [],
  };

  const [rule, setRule] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswers = async () => {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { all: true },
        });
        if (isMounted.current) {
          setQuickAnswers(data.quickAnswers);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchQuickAnswers();
    }
  }, [open]);

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const { data } = await api.get("/autoReplyRules");
        if (isMounted.current) {
          setAllRules(data);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchRules();
    }
  }, [open]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users");
        if (isMounted.current) {
          setUsers(data.users || []);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) {
      fetchUsers();
    }
  }, [open]);

  useEffect(() => {
    const fetchRule = async () => {
      if (!ruleId) {
        setRule(initialState);
        return;
      }

      try {
        const { data } = await api.get(`/autoReplyRules/${ruleId}`);
        if (isMounted.current) {
          setRule({
            name: data.name,
            keywords: data.keywords,
            matchType: data.matchType || "keywords",
            tagFilter: data.tagFilter || "all",
            active: data.active,
            priority: data.priority,
            whatsappId: data.whatsappId,
            quickAnswerId: data.quickAnswerId,
            mediaPath: data.mediaPath || "",
            mediaMimetype: data.mediaMimetype || "",
            mediaOriginalName: data.mediaOriginalName || "",
            mediaOrder: data.mediaOrder || "media_first",
            parentRuleId: data.parentRuleId || "",
            fireAfterAssigned: data.fireAfterAssigned || false,
            userScope: data.userScope || "all",
            userIds: (data.users || []).map((u) => u.id),
            windows: (data.windows || []).map((w) => ({
              windowType: w.windowType,
              daysOfWeek: w.daysOfWeek || "",
              specificDate: w.specificDate || "",
              startTime: w.startTime || "",
              endTime: w.endTime || "",
              endDayOffset: w.endDayOffset || 0,
            })),
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, open]);

  const handleClose = () => {
    onClose();
    setRule(initialState);
  };

  const handleFileSelected = async (event, setFieldValue) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const formData = new FormData();
    formData.append("media", file);

    setMediaUploading(true);
    try {
      const { data } = await api.post("/autoReplyRules/media", formData);
      setFieldValue("mediaPath", data.mediaPath);
      setFieldValue("mediaMimetype", data.mediaMimetype);
      setFieldValue("mediaOriginalName", data.mediaOriginalName);
    } catch (err) {
      toastError(err);
    } finally {
      setMediaUploading(false);
    }
  };

  const handleRemoveMedia = (setFieldValue) => {
    setFieldValue("mediaPath", "");
    setFieldValue("mediaMimetype", "");
    setFieldValue("mediaOriginalName", "");
  };

  // Opciones válidas de "regla padre": misma conexión, sin contarse a sí
  // misma ni a sus propias reglas descendientes (evita ofrecer un ciclo
  // obvio desde la UI; el backend igual lo valida).
  const getParentRuleOptions = (whatsappId) => {
    if (!whatsappId) return [];

    const descendantIds = new Set();
    if (ruleId) {
      let frontier = [ruleId];
      while (frontier.length > 0) {
        const children = allRules.filter((r) => frontier.includes(r.parentRuleId));
        frontier = children.map((r) => r.id);
        frontier.forEach((id) => descendantIds.add(id));
      }
    }

    return allRules.filter(
      (r) =>
        r.whatsappId === whatsappId &&
        r.id !== ruleId &&
        !descendantIds.has(r.id)
    );
  };

  const handleSaveRule = async (values) => {
    const payload = {
      ...values,
      parentRuleId: values.parentRuleId || null,
    };
    try {
      if (ruleId) {
        await api.put(`/autoReplyRules/${ruleId}`, payload);
      } else {
        const { data } = await api.post("/autoReplyRules", payload);
        if (onSave) {
          onSave(data);
        }
      }
      toast.success(i18n.t("autoReplyRuleModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {ruleId
            ? i18n.t("autoReplyRuleModal.title.edit")
            : i18n.t("autoReplyRuleModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={rule}
          enableReinitialize={true}
          validationSchema={AutoReplyRuleSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveRule(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            setFieldValue,
          }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.fieldContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("autoReplyRuleModal.form.name")}
                    name="name"
                    autoFocus
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                  error={touched.whatsappId && Boolean(errors.whatsappId)}
                >
                  <InputLabel>
                    {i18n.t("autoReplyRuleModal.form.whatsapp")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyRuleModal.form.whatsapp")}
                    name="whatsappId"
                  >
                    {whatsApps.map((whatsapp) => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        {whatsapp.name}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyRuleModal.form.matchType")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyRuleModal.form.matchType")}
                    name="matchType"
                  >
                    <MenuItem value="keywords">
                      {i18n.t("autoReplyRuleModal.form.matchTypeKeywords")}
                    </MenuItem>
                    <MenuItem value="any">
                      {i18n.t("autoReplyRuleModal.form.matchTypeAny")}
                    </MenuItem>
                  </Field>
                </FormControl>
                {values.matchType !== "any" && (
                  <div className={classes.fieldContainer}>
                    <Field
                      as={TextField}
                      label={i18n.t("autoReplyRuleModal.form.keywords")}
                      name="keywords"
                      error={touched.keywords && Boolean(errors.keywords)}
                      helperText={
                        (touched.keywords && errors.keywords) ||
                        i18n.t("autoReplyRuleModal.form.keywordsHelper")
                      }
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      fullWidth
                    />
                  </div>
                )}
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                  error={
                    touched.quickAnswerId && Boolean(errors.quickAnswerId)
                  }
                >
                  <InputLabel>
                    {i18n.t("autoReplyRuleModal.form.quickAnswer")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyRuleModal.form.quickAnswer")}
                    name="quickAnswerId"
                  >
                    {quickAnswers.map((quickAnswer) => (
                      <MenuItem key={quickAnswer.id} value={quickAnswer.id}>
                        {quickAnswer.shortcut}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>

                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyRuleModal.form.parentRule")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyRuleModal.form.parentRule")}
                    name="parentRuleId"
                  >
                    <MenuItem value="">
                      {i18n.t("autoReplyRuleModal.form.parentRuleNone")}
                    </MenuItem>
                    {getParentRuleOptions(values.whatsappId).map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </Field>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyRuleModal.form.parentRuleHelper")}
                  </Typography>
                </FormControl>

                <div className={classes.mediaSection}>
                  <Typography variant="subtitle2">
                    {i18n.t("autoReplyRuleModal.form.media")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyRuleModal.form.mediaHelper")}
                  </Typography>

                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    onChange={(e) => handleFileSelected(e, setFieldValue)}
                  />

                  {values.mediaPath ? (
                    <div className={classes.mediaPreview}>
                      {values.mediaMimetype?.startsWith("image/") ? (
                        <img
                          alt={values.mediaOriginalName}
                          src={`${getBackendUrl()}/public/${values.mediaPath}`}
                          className={classes.mediaThumb}
                        />
                      ) : (
                        <InsertDriveFileIcon />
                      )}
                      <Typography className={classes.mediaName} variant="body2">
                        {values.mediaOriginalName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMedia(setFieldValue)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      disabled={mediaUploading}
                      onClick={() => fileInputRef.current?.click()}
                      style={{ marginTop: 4 }}
                    >
                      {mediaUploading
                        ? i18n.t("autoReplyRuleModal.form.mediaUploading")
                        : i18n.t("autoReplyRuleModal.form.mediaUpload")}
                    </Button>
                  )}

                  {values.mediaPath && (
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                      margin="dense"
                    >
                      <InputLabel>
                        {i18n.t("autoReplyRuleModal.form.mediaOrder")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("autoReplyRuleModal.form.mediaOrder")}
                        name="mediaOrder"
                      >
                        <MenuItem value="media_first">
                          {i18n.t("autoReplyRuleModal.form.mediaOrderMediaFirst")}
                        </MenuItem>
                        <MenuItem value="text_first">
                          {i18n.t("autoReplyRuleModal.form.mediaOrderTextFirst")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  )}
                </div>

                <div className={classes.fieldContainer}>
                  <Field
                    as={TextField}
                    type="number"
                    label={i18n.t("autoReplyRuleModal.form.priority")}
                    name="priority"
                    helperText={i18n.t(
                      "autoReplyRuleModal.form.priorityHelper"
                    )}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </div>
                <Divider className={classes.windowsSection} />
                <div className={classes.windowsSection}>
                  <Typography variant="subtitle2">
                    {i18n.t("autoReplyRuleModal.form.windows.title")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("autoReplyRuleModal.form.windows.helper")}
                  </Typography>
                  <FieldArray name="windows">
                    {({ push, remove }) => (
                      <>
                        {values.windows.map((window, index) => {
                          const rowErrors = (errors.windows && errors.windows[index]) || {};
                          const rowTouched = (touched.windows && touched.windows[index]) || {};
                          const selectedDays = window.daysOfWeek
                            ? window.daysOfWeek.split(",").map(Number)
                            : [];

                          const toggleDay = (day) => {
                            const newDays = selectedDays.includes(day)
                              ? selectedDays.filter((d) => d !== day)
                              : [...selectedDays, day];
                            setFieldValue(
                              `windows[${index}].daysOfWeek`,
                              newDays.join(",")
                            );
                          };

                          const isAllDay = !window.startTime && !window.endTime;

                          const toggleAllDay = () => {
                            if (isAllDay) {
                              setFieldValue(`windows[${index}].startTime`, "00:00");
                              setFieldValue(`windows[${index}].endTime`, "23:59");
                            } else {
                              setFieldValue(`windows[${index}].startTime`, "");
                              setFieldValue(`windows[${index}].endTime`, "");
                            }
                          };

                          return (
                            <div className={classes.windowRow} key={index}>
                              <IconButton
                                size="small"
                                className={classes.removeButton}
                                onClick={() => remove(index)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                style={{ minWidth: 220 }}
                              >
                                <InputLabel>
                                  {i18n.t("autoReplyRuleModal.form.windows.type")}
                                </InputLabel>
                                <Select
                                  label={i18n.t("autoReplyRuleModal.form.windows.type")}
                                  value={window.windowType}
                                  onChange={(e) =>
                                    setFieldValue(
                                      `windows[${index}].windowType`,
                                      e.target.value
                                    )
                                  }
                                >
                                  <MenuItem value="weekly">
                                    {i18n.t(
                                      "autoReplyRuleModal.form.windows.typeWeekly"
                                    )}
                                  </MenuItem>
                                  <MenuItem value="specific_date">
                                    {i18n.t(
                                      "autoReplyRuleModal.form.windows.typeDate"
                                    )}
                                  </MenuItem>
                                </Select>
                              </FormControl>

                              {window.windowType === "weekly" ? (
                                <>
                                  <div className={classes.dayChips}>
                                    {DAY_OPTIONS.map((day) => (
                                      <Chip
                                        key={day.value}
                                        label={i18n.t(day.label)}
                                        size="small"
                                        color={
                                          selectedDays.includes(day.value)
                                            ? "primary"
                                            : "default"
                                        }
                                        onClick={() => toggleDay(day.value)}
                                      />
                                    ))}
                                  </div>
                                  {rowTouched.daysOfWeek && rowErrors.daysOfWeek && (
                                    <Typography variant="caption" color="error">
                                      {rowErrors.daysOfWeek}
                                    </Typography>
                                  )}
                                  <div className={classes.windowRowFields}>
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyRuleModal.form.windows.startTime"
                                      )}
                                      type="time"
                                      value={window.startTime}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].startTime`,
                                          e.target.value
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      InputLabelProps={{ shrink: true }}
                                      error={
                                        rowTouched.startTime &&
                                        Boolean(rowErrors.startTime)
                                      }
                                    />
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyRuleModal.form.windows.endTime"
                                      )}
                                      type="time"
                                      value={window.endTime}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].endTime`,
                                          e.target.value
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      InputLabelProps={{ shrink: true }}
                                      error={
                                        rowTouched.endTime &&
                                        Boolean(rowErrors.endTime)
                                      }
                                    />
                                    <TextField
                                      label={i18n.t(
                                        "autoReplyRuleModal.form.windows.endDayOffset"
                                      )}
                                      type="number"
                                      inputProps={{ min: 0, max: 6 }}
                                      value={window.endDayOffset}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `windows[${index}].endDayOffset`,
                                          Number(e.target.value)
                                        )
                                      }
                                      variant="outlined"
                                      margin="dense"
                                      style={{ width: 160 }}
                                    />
                                  </div>
                                  <Typography variant="caption" color="textSecondary">
                                    {i18n.t(
                                      "autoReplyRuleModal.form.windows.endDayOffsetHelper"
                                    )}
                                  </Typography>
                                </>
                              ) : (
                                <div className={classes.windowRowFields}>
                                  <TextField
                                    label={i18n.t(
                                      "autoReplyRuleModal.form.windows.date"
                                    )}
                                    type="date"
                                    value={window.specificDate}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `windows[${index}].specificDate`,
                                        e.target.value
                                      )
                                    }
                                    variant="outlined"
                                    margin="dense"
                                    InputLabelProps={{ shrink: true }}
                                    error={
                                      rowTouched.specificDate &&
                                      Boolean(rowErrors.specificDate)
                                    }
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={isAllDay}
                                        onChange={toggleAllDay}
                                        color="primary"
                                      />
                                    }
                                    label={i18n.t(
                                      "autoReplyRuleModal.form.windows.allDay"
                                    )}
                                  />
                                  {!isAllDay && (
                                    <>
                                      <TextField
                                        label={i18n.t(
                                          "autoReplyRuleModal.form.windows.startTime"
                                        )}
                                        type="time"
                                        value={window.startTime}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `windows[${index}].startTime`,
                                            e.target.value
                                          )
                                        }
                                        variant="outlined"
                                        margin="dense"
                                        InputLabelProps={{ shrink: true }}
                                      />
                                      <TextField
                                        label={i18n.t(
                                          "autoReplyRuleModal.form.windows.endTime"
                                        )}
                                        type="time"
                                        value={window.endTime}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `windows[${index}].endTime`,
                                            e.target.value
                                          )
                                        }
                                        variant="outlined"
                                        margin="dense"
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => push({ ...emptyWindow })}
                          style={{ marginTop: 8 }}
                        >
                          {i18n.t("autoReplyRuleModal.form.windows.add")}
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>
                    {i18n.t("autoReplyRuleModal.form.tagFilter")}
                  </InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("autoReplyRuleModal.form.tagFilter")}
                    name="tagFilter"
                  >
                    <MenuItem value="all">
                      {i18n.t("autoReplyRuleModal.form.tagFilterAll")}
                    </MenuItem>
                    <MenuItem value="with_tag">
                      {i18n.t("autoReplyRuleModal.form.tagFilterWithTag")}
                    </MenuItem>
                    <MenuItem value="without_tag">
                      {i18n.t("autoReplyRuleModal.form.tagFilterWithoutTag")}
                    </MenuItem>
                  </Field>
                </FormControl>
                <div className={classes.switchRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={handleChange}
                        name="active"
                        color="primary"
                      />
                    }
                    label={i18n.t("autoReplyRuleModal.form.active")}
                  />
                </div>
                <div className={classes.switchRow}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.fireAfterAssigned}
                        onChange={handleChange}
                        name="fireAfterAssigned"
                        color="primary"
                      />
                    }
                    label="Disparar aunque el ticket ya tenga agente asignado"
                  />
                </div>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                  margin="dense"
                >
                  <InputLabel>Agentes</InputLabel>
                  <Select
                    label="Agentes"
                    value={values.userScope}
                    onChange={(e) =>
                      setFieldValue("userScope", e.target.value)
                    }
                  >
                    <MenuItem value="all">Todos los agentes</MenuItem>
                    <MenuItem value="some">Agentes específicos</MenuItem>
                  </Select>
                  <Typography variant="caption" color="textSecondary">
                    Solo aplica una vez que el ticket ya tiene agente
                    asignado (ej. con "Disparar aunque el ticket ya tenga
                    agente asignado" activado arriba).
                  </Typography>
                </FormControl>
                {values.userScope === "some" && (
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    margin="dense"
                  >
                    <InputLabel>Agentes específicos</InputLabel>
                    <Select
                      multiple
                      label="Agentes específicos"
                      value={values.userIds}
                      onChange={(e) =>
                        setFieldValue("userIds", e.target.value)
                      }
                      renderValue={(selected) =>
                        users
                          .filter((u) => selected.includes(u.id))
                          .map((u) => u.name)
                          .join(", ")
                      }
                    >
                      {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          <Checkbox
                            size="small"
                            checked={values.userIds.includes(u.id)}
                          />
                          <ListItemText primary={u.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("autoReplyRuleModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {ruleId
                    ? i18n.t("autoReplyRuleModal.buttons.okEdit")
                    : i18n.t("autoReplyRuleModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AutoReplyRuleModal;
